import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, User, Mail, Phone, Euro, Calendar, MapPin, Clock, Users as UsersIcon } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  num_guests: number;
  broker_id: string;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
  brokers?: {
    full_name: string;
    email: string;
  };
}

interface AdminClientsTableProps {
  conversations: Conversation[];
  onViewChat: (conversation: Conversation) => void;
}

export const AdminClientsTable = ({ conversations, onViewChat }: AdminClientsTableProps) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all brokers in the brokerage
  const { data: brokers = [] } = useQuery({
    queryKey: ['brokers', profile?.brokerage_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brokers')
        .select('*')
        .eq('brokerage_id', profile?.brokerage_id);
      
      if (error) {
        console.error('Error fetching brokers:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!profile?.brokerage_id
  });

  // Mutation to assign broker to conversation
  const assignBrokerMutation = useMutation({
    mutationFn: async ({ conversationId, brokerId }: { conversationId: string; brokerId: string | null }) => {
      const { error } = await supabase
        .from('conversations')
        .update({ broker_id: brokerId })
        .eq('id', conversationId);

      if (error) {
        console.error('Error assigning broker:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-all-conversations'] });
    }
  });

  // Mutation for toggling conversation status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ conversationId, newStatus }: { conversationId: string; newStatus: boolean }) => {
      const { error } = await supabase
        .from('conversations')
        .update({ active: newStatus })
        .eq('id', conversationId);

      if (error) {
        console.error('Error updating conversation status:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Immediately invalidate and refresh both queries for faster updates
      queryClient.invalidateQueries({ queryKey: ['admin-all-conversations'] });
    },
    onError: (error) => {
      console.error('Failed to toggle conversation status:', error);
    }
  });

  const handleAssignBroker = (conversationId: string, brokerId: string) => {
    assignBrokerMutation.mutate({ conversationId, brokerId });
  };

  const handleToggleActive = async (conversationId: string, currentActive: boolean) => {
    toggleStatusMutation.mutate({ 
      conversationId, 
      newStatus: !currentActive 
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (e) {
      return dateString;
    }
  };

  const getAssignedBroker = (conversation: Conversation) => {
    if (conversation.brokers) {
      return conversation.brokers.full_name || 'Unknown Broker';
    }
    return 'Unassigned';
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
      <div className="p-6 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/80 to-gray-50/80">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg">
            <User className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Client Management</h2>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-100/80 border-b border-slate-200/50">
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">Client</TableHead>
              <TableHead className="font-semibold text-slate-700">Contact</TableHead>
              <TableHead className="font-semibold text-slate-700">Assigned Broker</TableHead>
              <TableHead className="font-semibold text-slate-700">Actions</TableHead>
              <TableHead className="font-semibold text-slate-700">Last Contact</TableHead>
              <TableHead className="font-semibold text-slate-700">Destination</TableHead>
              <TableHead className="font-semibold text-slate-700">Budget</TableHead>
              <TableHead className="font-semibold text-slate-700">Number of Guests</TableHead>
              <TableHead className="font-semibold text-slate-700">Travel Dates</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conversations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                  <User className="h-12 w-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className="text-slate-600">No client conversations found</p>
                </TableCell>
              </TableRow>
            ) : (
              conversations.map((conversation) => (
                <TableRow key={conversation.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100/50">
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={conversation.active}
                        onCheckedChange={() => handleToggleActive(conversation.id, conversation.active)}
                        disabled={toggleStatusMutation.isPending}
                        className="data-[state=checked]:bg-emerald-600"
                      />
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        conversation.active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {conversation.active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-800">
                      {conversation.clients?.full_name || 'Unknown Client'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {conversation.clients?.email && (
                        <div className="flex items-center text-sm text-slate-600">
                          <Mail className="h-3 w-3 mr-1 text-slate-500" />
                          {conversation.clients.email}
                        </div>
                      )}
                      {conversation.clients?.phone && (
                        <div className="flex items-center text-sm text-slate-600">
                          <Phone className="h-3 w-3 mr-1 text-slate-500" />
                          {conversation.clients.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={conversation.broker_id || 'unassigned'}
                      onValueChange={(value) => {
                        const brokerId = value === 'unassigned' ? null : value;
                        handleAssignBroker(conversation.id, brokerId);
                      }}
                      disabled={assignBrokerMutation.isPending}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue>
                          {getAssignedBroker(conversation)}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {brokers.map((broker) => (
                          <SelectItem key={broker.id} value={broker.id}>
                            {broker.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => onViewChat(conversation)}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2 hover:bg-emerald-50 border-emerald-200 text-emerald-700 hover:text-emerald-800 shadow-sm"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>View Chat</span>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-slate-600" />
                      <span className="text-slate-800 text-sm">
                        {formatDate(conversation.last_updated)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                      <span className="text-slate-800">
                        {conversation.destination || 'Not specified'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Euro className="h-4 w-4 mr-2 text-emerald-600" />
                      <span className="text-slate-800 font-semibold">
                        {conversation.budget_eur 
                          ? `€${conversation.budget_eur.toLocaleString()}`
                          : 'TBD'
                        }
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-2 text-blue-600" />
                      <span className="text-slate-800">
                        {conversation.num_guests || 'Not specified'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-slate-600" />
                      <span className="text-slate-800">
                        {conversation.start_date && conversation.end_date
                          ? `${formatDate(conversation.start_date)} - ${formatDate(conversation.end_date)}`
                          : 'Not specified'
                        }
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};